import type { RootState } from 'src/redux/store';

import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import Grid from '@mui/material/Grid2';
import { Box, Button, Dialog, DialogTitle, DialogContent } from '@mui/material';

import validationSchema from 'src/utils/validationSchema';

import { useTranslate } from 'src/locales';
import { useAppSelector } from 'src/hook/hooks';
import { useGetAssigneeList } from 'src/pages/tickets/hooks/useGetAssigneeList';
import { CustomAutocomplete } from 'src/pages/tickets/components/custom-autocomplete';

import { Form, Field } from 'src/components/hook-form';

import type { IAccount } from '../accounts.types';

type Props = {
  open: boolean;
  onClose: () => void;
  selectedUser?: IAccount;
  handleSubmitData: (data: IAccount) => void;
};

export function AccountForm({ open, onClose, selectedUser, handleSubmitData }: Props) {
  const { t } = useTranslate('translation');
  const { loginApiData } = useAppSelector((state) => (state as RootState).auth);

  type CreateContactSchemaType = {
    name: string;
    email: string;
    phone: string;
    crNumber: string;
    pincode: string;
    city: string;
    country: string;
    website: string;
    address: string;
    assignedId: string;
  };

  const fldList = [
    { name: 'name', required: true },
    { name: 'email', required: false },
    { name: 'phone', required: false },
    { name: 'crNumber', required: false },
    { name: 'pincode', required: false },
    { name: 'city', required: false },
    { name: 'country', required: false },
    { name: 'website', required: false },
    { name: 'address', required: false },
    { name: 'assignedId', required: true },
  ];

  const ContactSchema = validationSchema(fldList, t);

  const defaultValues: CreateContactSchemaType = {
    name: '',
    email: '',
    phone: '',
    crNumber: '',
    pincode: '',
    city: '',
    country: '',
    website: '',
    address: '',
    assignedId: '',
  };
  function convertNullToString(obj: any) {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [key, value === null ? '' : value])
    );
  }
  const methods = useForm<CreateContactSchemaType>({
    mode: 'all',
    resolver: zodResolver(ContactSchema),
    defaultValues,
    values: selectedUser
      ? { ...defaultValues, ...convertNullToString({ ...selectedUser, assignedId: '' }) } // Ensures `name` is never undefined
      : defaultValues,
  });

  const { reset, handleSubmit, setValue, watch } = methods;

  const watchAssignedId = watch('assignedId');

  const {
    _recordList,
    searchValue,
    isFetching,
    open: openList,
    listScrollHandler,
    searchValueHandler,
    openListHandler,
    closeListHandler,
    selectedAssigneeHandler,
  } = useGetAssigneeList({ setValue: setValue as any, selectedTicket: selectedUser });

  useEffect(() => {
    selectedAssigneeHandler(watchAssignedId);
  }, [watchAssignedId]);

  const onCloseHandler = () => {
    onClose();
    reset();
  };

  const onSubmit = handleSubmit(async (data: Partial<IAccount>) => {
    const updatedPhoneFld = data.phone?.includes('+') ? data.phone?.replace(/\+/g, '') : data.phone;
    const updatedData = { ...data, phone: updatedPhoneFld };

    if (data.assignedId) {
      if (typeof data.assignedId === 'object' && 'id' in data.assignedId) {
        updatedData['assignedId'] = data.assignedId.id ?? '';
        updatedData['assignedType'] = data.assignedId.isUser ? 'USER' : 'TEAM';
      } else {
        updatedData['assignedId'] = data.assignedId;
      }
    } else {
      delete updatedData?.assignedId;
    }
    handleSubmitData({
      ...updatedData,
      id: selectedUser?.id ?? '',
      ...(selectedUser?.id
        ? loginApiData && 'id' in loginApiData
          ? { modifiedById: loginApiData.id }
          : {}
        : loginApiData && 'id' in loginApiData
          ? { createdById: loginApiData.id }
          : {}),
    } as IAccount);
    onCloseHandler();
  });

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={onCloseHandler}
      sx={{
        '& .MuiPaper-rounded': {
          p: 0.5,
          boxShadow: '0px 0px 30px rgba(255, 255, 255, 0.1)',
        },
      }}
    >
      <Box>
        <DialogTitle sx={[(theme) => ({ p: theme.spacing(3, 3, 2, 3) })]}>
          {selectedUser && selectedUser?.name !== '' ? t('EDIT') : t('CREATE')} {t('ACCOUNT')}
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 1, pb: 0, border: 'none' }}>
          <Form methods={methods} onSubmit={onSubmit}>
            <Box sx={{ my: 2 }}>
              <Field.Text name="name" label={t('ACCOUNT_NAME')} />
            </Box>
            <Grid container spacing={2} sx={{ my: 2 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Field.Text name="email" label={t('EMAIL')} fullWidth />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Field.Phone
                  name="phone"
                  label={t('PHONE_NUM')}
                  placeholder={`${t('ENTER')} ${t('PHONE_NUM')}`}
                  fullWidth
                />
              </Grid>
            </Grid>
            <Grid container spacing={2} sx={{ my: 2 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Field.Text name="city" label={t('CITY')} fullWidth />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Field.Text name="country" label={t('COUNTRY')} fullWidth />
              </Grid>
            </Grid>
            <Grid container spacing={2} sx={{ my: 2 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Field.Text name="crNumber" label={t('CR_NUMBER')} fullWidth />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Field.Text name="pincode" label={t('PINCODE')} fullWidth />
              </Grid>
            </Grid>
            <Box sx={{ my: 2 }}>
              <Field.Text name="website" label={t('WEBSITE')} />
            </Box>
            <Box sx={{ my: 2 }}>
              <Field.Text name="address" label={t('ADDRESS')} />
            </Box>
            <Box sx={{ my: 2 }}>
              <Box sx={{ my: 2 }}>
                <CustomAutocomplete
                  name="assignedId"
                  label={t('ASSIGNED_TO')}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  placeholder="Select a User/Team"
                  options={_recordList}
                  searchValue={searchValue}
                  listScrollHandler={listScrollHandler}
                  searchValueHandler={searchValueHandler}
                  open={openList}
                  openListHandler={openListHandler}
                  closeListHandler={closeListHandler}
                  isFetching={isFetching}
                />
              </Box>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'end', my: 2 }}>
              <Button variant="outlined" color="inherit" onClick={onCloseHandler}>
                {t('CANCEL')}
              </Button>

              <Button type="submit" variant="contained" sx={{ ml: 2 }}>
                {t('SAVE')}
              </Button>
            </Box>
          </Form>
        </DialogContent>
      </Box>
    </Dialog>
  );
}

// type CreateContactSchemaType = zod.infer<typeof ContactSchema>;

// const ContactSchema = zod.object({
//   name: zod.string().min(1, { message: `${t('ACCOUNT_NAME')} ${t('IS_REQUIRED')}` }),
//   email: zod
//     .string()
//     .min(1, { message: `${t('EMAIL')} ${t('IS_REQUIRED')}` })
//     .email({ message: t('VALID_EMAIL') }),
//   phone: zod.string().min(1, { message: `${t('CONTACT_NUM')} ${t('IS_REQUIRED')}` }),
//   crNumber: zod.string().min(1, { message: `${t('CR_NUMBER')} ${t('IS_REQUIRED')}` }),
//   pincode: zod.string().min(1, { message: `${t('PINCODE')}` }),
//   city: zod.string().min(1, { message: `${t('CITY')}` }),
//   country: zod.string().min(1, { message: `${t('COUNTRY')}` }),
//   website: zod.string().min(1, { message: `${t('WEBSITE')} ${t('IS_REQUIRED')}` }),
//   address: zod.string().min(1, { message: `${t('ADDRESS')} ${t('IS_REQUIRED')}` }),
// });
